import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { DateRange, DayPicker as ReactDayPicker } from "react-day-picker"
import { useForm } from "react-hook-form"

import useIsDesktop from "@hooks/useIsDesktop"

import { ifSpaceOrEnter } from "@utils/keyboard"

import { Button, Divider, Label } from "@atoms"

import { Popover } from "@molecules"
import {
    SHARED_CLASS_NAMES,
    SHARED_COMPONENTS,
    SHARED_DAY_PICKER_PROPS,
} from "@molecules/DatePicker/DatePicker.constants"
import { useDatePickerOpenState } from "@molecules/DatePicker/DatePicker.hooks"
import parentStyles from "@molecules/DatePicker/DatePicker.module.scss"
import { formatSingleDate } from "@molecules/DatePicker/DatePicker.utils"
import { Form } from "@molecules/Form/Form"
import DateInput from "@molecules/Form/inputs/DateInput/DateInput"

import styles from "./DatePickerRange.module.scss"
import { DatePickerRangeProps } from "./DatePickerRange.types"

interface RangeFormData {
    startDateInput: string
    endDateInput: string
}

export default function DatePickerRange(props: DatePickerRangeProps) {
    const {
        className,
        classNames,
        showOutsideDays = true,
        children,
        open: controlledOpen,
        onOpenChange = () => null,
        onSelect,
        defaultSelected,
        selected,
        numberOfMonths = 2,
        ...rest
    } = props

    const [isOpen, setIsOpen] = useDatePickerOpenState({ controlledOpen, onOpenChange })

    const initialRange = selected ?? defaultSelected

    const [parsedRange, setParsedRange] = useState<DateRange | undefined>(initialRange)
    const [month, setMonth] = useState<Date | undefined>(initialRange?.from ?? new Date())
    const [tempRange, setTempRange] = useState<DateRange | undefined>(initialRange)

    const isDesktop = useIsDesktop()

    // Sync internal state when controlled `selected` changes. Skip the initial mount so
    // uncontrolled usage (`defaultSelected` only) isn't overwritten with `undefined`.
    const hasMountedSelectedSyncRef = useRef(false)
    useEffect(() => {
        if (!hasMountedSelectedSyncRef.current) {
            hasMountedSelectedSyncRef.current = true
            return
        }
        if (selected) {
            setParsedRange(selected)
            setMonth(selected.from ?? new Date())
        } else {
            setParsedRange(undefined)
            setMonth(new Date())
        }
    }, [selected])

    const defaultStartValue = initialRange?.from ? formatSingleDate(initialRange.from) : ""
    const defaultEndValue = initialRange?.to ? formatSingleDate(initialRange.to) : ""

    const form = useForm<RangeFormData>({
        defaultValues: {
            startDateInput: defaultStartValue,
            endDateInput: defaultEndValue,
        },
    })

    const handleRangeSelect = (range: DateRange | undefined) => {
        onSelect(range)
        if (range?.from && range?.to) {
            setIsOpen(false)
        }
    }

    const onCalendarSelect = (range: DateRange | undefined) => {
        setParsedRange(range)
        setTempRange(range)

        if (range?.from) {
            form.setValue("startDateInput", formatSingleDate(range.from))
        }
        if (range?.to) {
            form.setValue("endDateInput", formatSingleDate(range.to))
        }

        // On desktop, apply immediately when both dates are selected
        if (isDesktop && range?.from && range?.to) {
            handleRangeSelect(range)
        }
    }

    const onStartDateChange = (date: Date | null) => {
        const newRange: DateRange | undefined = date ? { from: date, to: tempRange?.to } : undefined
        setTempRange(newRange)
        setParsedRange(newRange)
        if (date) {
            setMonth(date)
        }
    }

    const onEndDateChange = (date: Date | null) => {
        const newRange: DateRange | undefined = tempRange?.from
            ? { from: tempRange.from, to: date ?? undefined }
            : undefined
        setTempRange(newRange)
        setParsedRange(newRange)
    }

    const onApplyButtonClick = () => {
        if (tempRange?.from && tempRange?.to) {
            handleRangeSelect(tempRange)
        }
    }

    const triggerRef = useRef<HTMLDivElement>(null)

    const focusInnerInput = () => {
        triggerRef.current?.querySelector("input")?.focus()
    }

    return (
        <Popover.Root isOpen={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger>
                <div
                    ref={triggerRef}
                    tabIndex={0}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-haspopup="dialog"
                    className={styles.trigger}
                    onClick={() => setIsOpen(true)}
                    onKeyDown={(event) => {
                        if (isOpen || event.target !== event.currentTarget) {
                            return
                        }
                        ifSpaceOrEnter(event, () => {
                            setIsOpen(true)
                            focusInnerInput()
                        })
                    }}
                >
                    {children}
                </div>
            </Popover.Trigger>

            <Popover.Viewport
                align="start"
                className={parentStyles.popover}
                onOpenAutoFocus={(event) => {
                    event.preventDefault()
                }}
            >
                <div className={parentStyles.base}>
                    <div className={parentStyles.calendar} onMouseDown={(event) => event.preventDefault()}>
                        <ReactDayPicker
                            {...rest}
                            {...SHARED_DAY_PICKER_PROPS}
                            showOutsideDays={showOutsideDays}
                            className={clsx(parentStyles.picker, className)}
                            selected={parsedRange}
                            month={month}
                            mode="range"
                            numberOfMonths={numberOfMonths}
                            onMonthChange={setMonth}
                            onSelect={onCalendarSelect}
                            classNames={{
                                ...SHARED_CLASS_NAMES,
                                ...classNames,
                            }}
                            components={SHARED_COMPONENTS}
                        />
                    </div>

                    {!isDesktop && (
                        <>
                            <Divider />
                            <div className={styles.mobileInput}>
                                <Form form={form} className={styles.form}>
                                    <Label label="Date range" variant="subtle" />
                                    <div className={styles.inputGroup}>
                                        <DateInput
                                            name="startDateInput"
                                            size="md"
                                            label="Start date"
                                            onDateChange={onStartDateChange}
                                            placeholder="Start date..."
                                            isDisabled={false}
                                            isControlled={true}
                                            isGrouped={true}
                                            groupPosition="start"
                                        />
                                        <DateInput
                                            name="endDateInput"
                                            size="md"
                                            label="End date"
                                            onDateChange={onEndDateChange}
                                            placeholder="End date..."
                                            isDisabled={false}
                                            isControlled={true}
                                            isGrouped={true}
                                            groupPosition="end"
                                        />
                                    </div>
                                    <Button
                                        variant="solid"
                                        size="lg"
                                        onClick={onApplyButtonClick}
                                        className={styles.applyButton}
                                    >
                                        Apply
                                    </Button>
                                </Form>
                            </div>
                        </>
                    )}
                </div>
            </Popover.Viewport>
        </Popover.Root>
    )
}
